import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'clean-up-text-copied-from-pdf',
  title: 'How to Clean Up Text Copied from a PDF',
  description:
    'Fix broken line breaks, hyphenated words, extra spaces, smart quotes and invisible characters in text copied from a PDF, in a few clicks and for free.',
  category: 'Text editor',
  publishedDate: '2026-11-03',
  affiliateCategory: 'writing',
  body: `
Text copied out of a PDF rarely pastes cleanly. Lines break in the middle of sentences, words are split with hyphens, spacing is uneven and odd characters sneak in. The [Text Editor](/text-editor) fixes all of this with one-click tools. Paste your text, then work through this checklist.

## 1. Fix the line breaks (the big one)

PDFs store text line by line, exactly as it was laid out on the page, so copied text has a hard line break at the end of every visual line:

\`\`\`
The quarterly results show a significant im-
provement in operating margin, driven by
lower costs in the second half.
\`\`\`

Open **Fix line breaks & whitespace** and click **Fix PDF line breaks**. It:

- joins the lines of each paragraph back into one,
- re-joins words split by an end-of-line hyphen (\`im-\` + \`provement\` → \`improvement\`),
- keeps blank lines between paragraphs, and keeps bullet and numbered list items on their own lines.

## 2. Tidy the spacing

Still in **Fix line breaks & whitespace**:

- **Collapse multiple spaces** — removes the extra spaces PDFs use for justification and columns.
- **Collapse blank lines** — turns runs of empty lines into a single paragraph break.
- **Trim each line** / **Remove trailing spaces** — clean up the edges of every line.

## 3. Straighten quotes and remove junk characters

Under **Clean up**:

- **Smart quotes → straight** — converts curly quotes, long dashes and the "…" character to plain keyboard equivalents. Essential before pasting into code, spreadsheets or forms.
- **Remove invisible characters** — strips zero-width spaces, soft hyphens and byte-order marks that are invisible but break searches and comparisons.
- **Remove accents (é → e)** and **Remove non-ASCII characters** — useful for file names, IDs and systems that only accept plain ASCII.

## 4. Work on part of the text only

Every tool applies to your **selection** if you have one — the chip above the tools switches to **Applies to selection**. Select just a table or a single paragraph to fix it without touching the rest.

Made a mistake? **Ctrl+Z** (⌘Z on Mac) undoes any tool in one step.

## 5. Handy extras

- **Remove line numbers / bullets** (under *Sort & edit lines*) strips "1." or "•" prefixes from copied lists.
- **One sentence per line** makes long paragraphs easy to review or translate.
- **Strip HTML tags** cleans text copied from web pages or emails.
- **Find & replace** (Ctrl+F) handles anything specific to your document — see the [regex cheat sheet](/guides/find-and-replace-regex-cheat-sheet) for powerful patterns.

## Skip the copy-paste entirely

Instead of copying from a PDF viewer, convert the file with the [Converter](/) and click **Open in Text Editor** — or open the PDF directly in the editor with the folder button. You'll get cleaner text to start with, and you can still use every tool above.
`,
};

export default guide;
